import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomeOrganization } from '../queries/some-organizations.query';

import { useFindAllDeletedExternalOrganizationsQuery } from './useFindAllDeletedExternalOrganizationsQuery.hook';
import { useFindAllDeletedInternalOrganizationsQuery } from './useFindAllDeletedInternalOrganizationsQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{
        findSomeOrganizations: readonly ThirdPartiesCmp_SomeOrganization[];
      }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllDeletedOrganizationsQuery = (
  enabled = true,
): UnionResult => {
  const internalQuery = useFindAllDeletedInternalOrganizationsQuery(enabled);
  const externalQuery = useFindAllDeletedExternalOrganizationsQuery(enabled);

  const memoizedResult = useMemo(() => {
    const internalData = internalQuery.data?.findSomeOrganizations;
    const externalData = externalQuery.data?.findSomeOrganizations;

    const data =
      internalData || externalData
        ? {
            findSomeOrganizations: [
              ...(internalData ?? EMPTY_ARRAY),
              ...(externalData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading = internalQuery.isLoading || externalQuery.isLoading;
    const isSuccess = internalQuery.isSuccess && externalQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    internalQuery.data?.findSomeOrganizations,
    internalQuery.isLoading,
    internalQuery.isSuccess,
    externalQuery.data?.findSomeOrganizations,
    externalQuery.isLoading,
    externalQuery.isSuccess,
  ]);

  return memoizedResult;
};
